import Vue from 'vue';
import { VuexBuilder } from '@helpers/vuexBuilder';

const state = {
	formsList: [],
	formsById: {},
	selfAssignableForms: [],
	assignedFormsByEmployeeId: {},
	submissionsById: {},
	operations: {
		list: false,
		listSelfAssignable: false,
		listAssignedToEmployee: false,
		create: false,
		appendVersion: false,
		archive: false,
		unarchive: false,
		getLatest: false,
		getByVersion: false,
		createSubmission: false,
		startSelfAssignment: false,
		sendNewLink: false,
		cancelSubmission: false
	}
};

const getters = {
	formsList: state => state.formsList,
	selfAssignableForms: state => state.selfAssignableForms,
	formById: state => id => state.formsById[id] || null,
	assignedFormsForEmployee: state => employeeId => state.assignedFormsByEmployeeId[employeeId] || [],
	submissionById: state => id => state.submissionsById[id] || null,
	isListing: state => state.operations.list,
	isListingSelfAssignable: state => state.operations.listSelfAssignable,
	isListingAssignedToEmployee: state => state.operations.listAssignedToEmployee,
	isCreating: state => state.operations.create,
	isAppendingVersion: state => state.operations.appendVersion,
	isArchiving: state => state.operations.archive,
	isUnarchiving: state => state.operations.unarchive,
	isGettingLatest: state => state.operations.getLatest,
	isGettingByVersion: state => state.operations.getByVersion,
	isCreatingSubmission: state => state.operations.createSubmission,
	isStartingSelfAssignment: state => state.operations.startSelfAssignment,
	isSendingNewLink: state => state.operations.sendNewLink,
	isCancellingSubmission: state => state.operations.cancelSubmission
};

const actions = {
	async list({ commit, rootState }) {
		return await new VuexBuilder(commit)
			.operationKey('list')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/forms`))
			.commitData('SET_LIST')
			.execute();
	},
	async listSelfAssignable({ commit, rootState }) {
		return await new VuexBuilder(commit)
			.operationKey('listSelfAssignable')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/forms/self-assignable`))
			.commitData('SET_SELF_ASSIGNABLE_LIST')
			.errorToast('Failed to load available forms.')
			.execute();
	},
	async listAssignedToEmployee({ commit, rootState }, employeeId) {
		commit('START_OPERATION', 'listAssignedToEmployee');

		try {
			const response = await Vue.axios.get(`/api/${rootState.tenantId}/employees/${employeeId}/profile/forms`);
			commit('SET_ASSIGNED_FORMS_FOR_EMPLOYEE', {
				employeeId,
				forms: response.data
			});
			return response.data;
		} finally {
			commit('FINISH_OPERATION', 'listAssignedToEmployee');
		}
	},
	async create({ commit, rootState }, payload) {
		return await new VuexBuilder(commit)
			.operationKey('create')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms`, payload))
			.commitData('UPSERT_FORM')
			.successToast('Form created successfully.')
			.errorToast('Failed to create form.')
			.execute();
	},
	async appendVersion({ commit, rootState }, { formId, definition }) {
		return await new VuexBuilder(commit)
			.operationKey('appendVersion')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms/${formId}/versions`, { definition }))
			.commitData('UPSERT_FORM')
			.successToast('Form version created successfully.')
			.errorToast('Failed to create form version.')
			.execute();
	},
	async archive({ commit, rootState }, formId) {
		const result = await new VuexBuilder(commit)
			.operationKey('archive')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms/${formId}/archive`))
			.successToast('Form archived successfully.')
			.errorToast('Failed to archive form.')
			.execute();

		if (result.isSuccess) {
			commit('SET_FORM_ARCHIVED', formId);
		}

		return result;
	},
	async unarchive({ commit, rootState }, formId) {
		const result = await new VuexBuilder(commit)
			.operationKey('unarchive')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms/${formId}/unarchive`))
			.successToast('Form unarchived successfully.')
			.errorToast('Failed to unarchive form.')
			.execute();

		if (result.isSuccess) {
			commit('SET_FORM_ACTIVE', formId);
		}

		return result;
	},
	async getLatest({ commit, rootState }, formId) {
		return await new VuexBuilder(commit)
			.operationKey('getLatest')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/forms/${formId}`))
			.commitData('UPSERT_FORM')
			.execute();
	},
	async getByVersion({ commit, rootState }, { formId, versionNo }) {
		return await new VuexBuilder(commit)
			.operationKey('getByVersion')
			.request(() => Vue.axios.get(`/api/${rootState.tenantId}/forms/${formId}/versions/${versionNo}`))
			.commitData('UPSERT_FORM')
			.execute();
	},
	async createSubmission({ commit, rootState }, { formId, values, parties, successMessage = 'Form submission created successfully.', errorMessage = 'Failed to create form submission.' }) {
		return await new VuexBuilder(commit)
			.operationKey('createSubmission')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms/${formId}/submissions`, { values, parties }))
			.commitData('UPSERT_SUBMISSION')
			.successToast(successMessage)
			.errorToast(errorMessage)
			.execute();
	},
	async startSelfAssignment({ commit, rootState }, formId) {
		return await new VuexBuilder(commit)
			.operationKey('startSelfAssignment')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/forms/${formId}/self-assignments`))
			.commitData('UPSERT_SUBMISSION')
			.errorToast('Failed to start form.')
			.execute();
	},
	async sendNewLink({ commit, rootState }, { employeeId, submissionId }) {
		return await new VuexBuilder(commit)
			.operationKey('sendNewLink')
			.request(() => Vue.axios.post(`/api/${rootState.tenantId}/employees/${employeeId}/profile/forms/${submissionId}/send-new-link`))
			.successToast('New form link sent.')
			.errorToast('Failed to send new form link.')
			.execute();
	},
	async cancelSubmission({ commit, rootState }, { employeeId, submissionId }) {
		return await new VuexBuilder(commit)
			.operationKey('cancelSubmission')
			.request(() => Vue.axios.delete(`/api/${rootState.tenantId}/employees/${employeeId}/profile/forms/${submissionId}`))
			.successToast('Form assignment cancelled.')
			.errorToast('Failed to cancel form assignment.')
			.execute();
	}
};

const mutations = {
	START_OPERATION(state, operationKey) {
		state.operations[operationKey] = true;
	},
	FINISH_OPERATION(state, operationKey) {
		state.operations[operationKey] = false;
	},
	SET_LIST(state, forms) {
		const list = Array.isArray(forms) ? forms : [];
		state.formsList = list;
		for (const form of list) {
			if (form && form.id) {
				Vue.set(state.formsById, form.id, form);
			}
		}
	},
	SET_SELF_ASSIGNABLE_LIST(state, forms) {
		const list = Array.isArray(forms) ? forms : [];
		state.selfAssignableForms = list;
		for (const form of list) {
			if (form && form.id) {
				Vue.set(state.formsById, form.id, form);
			}
		}
	},
	SET_ASSIGNED_FORMS_FOR_EMPLOYEE(state, { employeeId, forms }) {
		Vue.set(state.assignedFormsByEmployeeId, employeeId, Array.isArray(forms) ? forms : []);
	},
	UPSERT_FORM(state, form) {
		if (!form || !form.id) return;
		Vue.set(state.formsById, form.id, form);
	},
	SET_FORM_ARCHIVED(state, formId) {
		const listItem = state.formsList.find(form => form && form.id === formId);
		if (listItem) {
			Vue.set(listItem, 'status', 'Archived');
			Vue.set(listItem, 'lastUpdatedDateUtc', new Date().toISOString());
		}

		const form = state.formsById[formId];
		if (form) {
			Vue.set(form, 'status', 'Archived');
			Vue.set(form, 'lastUpdatedDateUtc', new Date().toISOString());
		}
	},
	SET_FORM_ACTIVE(state, formId) {
		const listItem = state.formsList.find(form => form && form.id === formId);
		if (listItem) {
			Vue.set(listItem, 'status', 'Active');
			Vue.set(listItem, 'lastUpdatedDateUtc', new Date().toISOString());
		}

		const form = state.formsById[formId];
		if (form) {
			Vue.set(form, 'status', 'Active');
			Vue.set(form, 'lastUpdatedDateUtc', new Date().toISOString());
		}
	},
	UPSERT_SUBMISSION(state, submission) {
		if (!submission || (!submission.submissionId && !submission.id)) return;
		const submissionId = submission.id || submission.submissionId;
		Vue.set(state.submissionsById, submissionId, submission);
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
